import { useEffect, useRef } from "react";
import { keyframes, styled } from "@pigment-css/react";
import { useLocale, useTranslations } from "@acto/web-common/locales";
import { formatNumericDate } from "@acto/taskpane/field-sales-2/utils/format";

const fadeIn = keyframes({
  from: { opacity: 0, transform: "translate(-50%, 8px)" },
  to: { opacity: 1, transform: "translate(-50%, 0)" },
});

const Root = styled("div")(({ theme }) => ({
  position: "fixed",
  left: "50%",
  bottom: `calc(var(--bottom-nav-opaque-height, ${theme.fieldSalesTwo.spacing[72]}) + ${theme.fieldSalesTwo.spacing[16]})`,
  transform: "translateX(-50%)",
  zIndex: 1200,
  boxSizing: "border-box",
  width: `calc(100% - ${theme.fieldSalesTwo.spacing[32]})`,
  maxWidth: "24rem",
  padding: `${theme.fieldSalesTwo.spacing[32]} ${theme.fieldSalesTwo.spacing[24]}`,
  borderRadius: theme.fieldSalesTwo.radius[38],
  border: `${theme.strokeWidth.thin} solid ${theme.fieldSalesTwo.colors.surface.glassBorderStrong}`,
  background: theme.fieldSalesTwo.colors.surface.glassStrong,
  backdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  WebkitBackdropFilter: `blur(${theme.fieldSalesTwo.effects.blurSm})`,
  boxShadow: theme.fieldSalesTwo.shadows.glass,
  animation: `${fadeIn} ${theme.fieldSalesTwo.motion.duration.base} ${theme.fieldSalesTwo.motion.easing.emphasized}`,
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}));

const Message = styled("p")(({ theme }) => ({
  margin: 0,
  fontFamily: theme.fieldSalesTwo.font.family,
  fontSize: theme.fieldSalesTwo.font.size[17],
  fontWeight: theme.fieldSalesTwo.font.weight[440],
  lineHeight: theme.fieldSalesTwo.font.lineHeight[148],
  letterSpacing: theme.fieldSalesTwo.font.letterSpacing.subtle,
  color: theme.fieldSalesTwo.colors.neutral[900],
  textAlign: "center",
  overflowWrap: "break-word",
}));

interface MeetingDeletedBannerProps {
  open: boolean;
  /** ISO date (YYYY-MM-DD) of the deleted meeting, shown in the confirmation copy. */
  date: string;
  onClose: () => void;
  /** Auto-dismiss delay in ms. */
  durationMs?: number;
}

/** Transient card confirming a meeting was deleted (e.g. "Das Meeting am 22.05.2026 wurde entfernt").
 * Local-state driven, auto-dismisses after `durationMs`. */
export function MeetingDeletedBanner({ open, date, onClose, durationMs = 3000 }: MeetingDeletedBannerProps) {
  const t = useTranslations("fieldSalesTwo.upcomingMeetings");
  const { locale } = useLocale();

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // `date` is in the deps so a second deletion while the banner is still up restarts the timer,
  // rather than inheriting the first banner's remaining time and flashing away.
  useEffect(() => {
    if (!open) return;

    const timer = setTimeout(() => onCloseRef.current(), durationMs);
    return () => clearTimeout(timer);
  }, [open, date, durationMs]);

  if (!open) return null;

  return (
    <Root role="status" aria-live="polite">
      <Message>{t("meetingDeleted", { date: formatNumericDate(date, locale) })}</Message>
    </Root>
  );
}
